/**
 * WordPuzzleAdmin.js — Admin panel for managing Word Puzzle levels
 * Standalone module: create/edit levels, auto-generate, settings, stats
 */

import React, { useState, useEffect, useCallback } from 'react';
import IconPicker from './IconPicker';

const WordPuzzleAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [activeTab, setActiveTab] = useState('levels');
  const [levels, setLevels] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Icon picker state
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [iconPickerTarget, setIconPickerTarget] = useState('training');

  // Level editor state
  const [editingLevel, setEditingLevel] = useState(null);
  const [levelWords, setLevelWords] = useState([{ english: '', darija: '' }]);
  const [levelTitle, setLevelTitle] = useState('');
  const [levelDifficulty, setLevelDifficulty] = useState('easy');
  const [levelNumber, setLevelNumber] = useState('');
  const [levelIsTraining, setLevelIsTraining] = useState(false);
  const [levelFreeHints, setLevelFreeHints] = useState(false);
  const [levelRevealCount, setLevelRevealCount] = useState(0);
  const [previewGrid, setPreviewGrid] = useState(null);

  // Auto-generate state
  const [autoWordCount, setAutoWordCount] = useState(4);
  const [autoDifficulty, setAutoDifficulty] = useState('easy');
  const [autoPreview, setAutoPreview] = useState(null);

  // How to Play state
  const [htpSteps, setHtpSteps] = useState([]);
  const [htpEditing, setHtpEditing] = useState(null);
  const [htpIcon, setHtpIcon] = useState('📝');
  const [htpTitle, setHtpTitle] = useState('');
  const [htpDesc, setHtpDesc] = useState('');

  // Design Puzzle / Game Card state
  const [gameCard, setGameCard] = useState({ icon: '🧩', icon_url: '', name: 'Word Puzzle', description: 'Swipe Darija letters to solve crossword puzzles!', is_visible: true, is_pro: false, card_gradient: '' });
  const [gcSaving, setGcSaving] = useState(false);

  // Known words state
  const [knownWords, setKnownWords] = useState([]);
  const [knownWordsText, setKnownWordsText] = useState('');
  const [discoveredWords, setDiscoveredWords] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [discoverFilter, setDiscoverFilter] = useState('all');
  const [discoverLevels, setDiscoverLevels] = useState([]);

  // Backgrounds state
  const [backgrounds, setBackgrounds] = useState([]);
  const [bgName, setBgName] = useState('');
  const [bgType, setBgType] = useState('gradient');
  const [bgValue, setBgValue] = useState('');
  const [bgIsDefault, setBgIsDefault] = useState(false);
  const [bgIsLocked, setBgIsLocked] = useState(false);
  const [bgPrice, setBgPrice] = useState(0);
  const [bgEditing, setBgEditing] = useState(null);
  const [bgUploading, setBgUploading] = useState(false);

  // Hex styles state
  const [hexStyles, setHexStyles] = useState([]);
  const [hsName, setHsName] = useState('');
  const [hsBg, setHsBg] = useState('');
  const [hsBorder, setHsBorder] = useState('');
  const [hsText, setHsText] = useState('#1e293b');
  const [hsPrice, setHsPrice] = useState(200);
  const [hsEditing, setHsEditing] = useState(null);
  const [shopDeleteConfirm, setShopDeleteConfirm] = useState(null); // { id, name, type } for styled delete modal

  // Image picker state
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [pickerImages, setPickerImages] = useState({ icons: [], avatars: [], folders: [] });
  const [pickerTab, setPickerTab] = useState('icons');
  const [pickerLoading, setPickerLoading] = useState(false);

  // Player management state
  const [playerSearch, setPlayerSearch] = useState('');
  const [playerResults, setPlayerResults] = useState([]);
  const [playerTotal, setPlayerTotal] = useState(0);
  const [playerPage, setPlayerPage] = useState(1);
  const [playerPages, setPlayerPages] = useState(1);
  const [selectedPlayer, setSelectedPlayer] = useState(null); // progress data
  const [selectedPlayerInfo, setSelectedPlayerInfo] = useState(null); // user info
  const [playerLoading, setPlayerLoading] = useState(false);

  const headers = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };

  // Swap order of two items (for reordering)
  const swapOrder = async (endpoint, items, index, direction, reloadFn) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const a = items[index], b = items[target];
    try {
      await Promise.all([
        fetch(`${API}/admin/word-puzzle/${endpoint}/${a.id}`, { method: 'PUT', headers, body: JSON.stringify({ order: b.order ?? target }) }),
        fetch(`${API}/admin/word-puzzle/${endpoint}/${b.id}`, { method: 'PUT', headers, body: JSON.stringify({ order: a.order ?? index }) })
      ]);
      reloadFn();
    } catch (e) { showNotification('Reorder failed', 'error'); }
  };

  // Load images from Image Manager for picker
  const loadPickerImages = async () => {
    setPickerLoading(true);
    try {
      const [iconsRes, avatarsRes, foldersRes] = await Promise.all([
        fetch(`${API}/admin/icons`, { headers }),
        fetch(`${API}/admin/avatar-images`, { headers }),
        fetch(`${API}/admin/image-folders`, { headers })
      ]);
      const iconsData = iconsRes.ok ? await iconsRes.json() : { icons: [] };
      const avatarsData = avatarsRes.ok ? await avatarsRes.json() : { avatar_images: [] };
      const foldersData = foldersRes.ok ? await foldersRes.json() : { folders: [] };

      // Load assets for each folder
      const foldersWithAssets = await Promise.all(
        (foldersData.folders || []).map(async (f) => {
          try {
            const res = await fetch(`${API}/admin/image-assets/${f.id}`, { headers });
            const data = res.ok ? await res.json() : { assets: [] };
            return { ...f, assets: data.assets || [] };
          } catch { return { ...f, assets: [] }; }
        })
      );

      setPickerImages({
        icons: iconsData.icons || [],
        avatars: avatarsData.avatar_images || [],
        folders: foldersWithAssets
      });
    } catch (e) { showNotification('Failed to load images', 'error'); }
    finally { setPickerLoading(false); }
  };

  const bg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const text = isDarkMode ? 'text-gray-200' : 'text-gray-800';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-800';

  // ── Load data ─────────────────────────────────────────────────────────
  const loadLevels = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/levels`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLevels(data.levels || []);
      }
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/settings`, { headers });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  const loadPlayers = useCallback(async (search = '', page = 1) => {
    setPlayerLoading(true);
    try {
      const res = await fetch(`${API}/admin/word-puzzle/players/search?q=${encodeURIComponent(search)}&page=${page}`, { headers });
      if (res.ok) {
        const d = await res.json();
        setPlayerResults(d.users);
        setPlayerTotal(d.total);
        setPlayerPages(d.pages);
        setPlayerPage(d.page);
      }
    } catch (e) { console.error(e); }
    finally { setPlayerLoading(false); }
  }, [API, adminToken]);

  const loadBackgrounds = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/backgrounds`, { headers });
      if (res.ok) {
        const data = await res.json();
        setBackgrounds(data.backgrounds || []);
      }
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  const loadHexStyles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/hex-styles`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHexStyles(data.hex_styles || []);
      }
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  const loadGameCard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/game-settings`, { headers });
      if (res.ok) {
        const data = await res.json();
        setGameCard({
          icon: data.icon || '🧩',
          icon_url: data.icon_url || '',
          name: data.name || 'Word Puzzle',
          description: data.description || '',
          is_visible: data.is_visible !== false,
          is_pro: data.is_pro || false,
          card_gradient: data.card_gradient || ''
        });
      }
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  const loadHowToPlay = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/how-to-play`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHtpSteps(data.steps || []);
      }
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  const loadKnownWords = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/known-words`, { headers });
      if (res.ok) {
        const data = await res.json();
        setKnownWords(data.words || []);
        setKnownWordsText((data.words || []).map(w => `${w.darija} = ${w.english}`).join('\n'));
      }
    } catch (e) { console.error(e); }
  }, [API, adminToken]);

  useEffect(() => {
    Promise.all([loadLevels(), loadStats(), loadSettings(), loadHowToPlay(), loadGameCard(), loadBackgrounds(), loadKnownWords(), loadHexStyles()]).finally(() => setLoading(false));
  }, [loadLevels, loadStats, loadSettings, loadHowToPlay, loadGameCard, loadBackgrounds, loadKnownWords]);

  // Auto-load players when Players tab is opened
  useEffect(() => {
    if (activeTab === 'players') loadPlayers('', 1);
  }, [activeTab]);

  // ── Add/remove word rows ──────────────────────────────────────────────
  const addWordRow = () => setLevelWords(prev => [...prev, { english: '', darija: '' }]);
  const removeWordRow = (i) => setLevelWords(prev => prev.filter((_, idx) => idx !== i));
  const updateWord = (i, field, value) => {
    setLevelWords(prev => prev.map((w, idx) => idx === i ? { ...w, [field]: value } : w));
  };

  // ── Save level ────────────────────────────────────────────────────────
  const saveLevel = async () => {
    const validWords = levelWords.filter(w => w.english.trim() && w.darija.trim());
    if (validWords.length < 2) {
      showNotification('Need at least 2 words', 'error');
      return;
    }

    const body = {
      words: validWords,
      title: levelTitle || undefined,
      difficulty: levelDifficulty,
      background: editingLevel?.background || undefined,
      is_training: levelIsTraining,
      free_hints: levelFreeHints,
      reveal_word_count: levelRevealCount,
      ...(levelNumber ? { level_number: Number(levelNumber) } : {})
    };

    try {
      const url = editingLevel
        ? `${API}/admin/word-puzzle/levels/${editingLevel.id}`
        : `${API}/admin/word-puzzle/levels`;
      const method = editingLevel ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (res.ok) {
        const data = await res.json();
        if (data.warning) {
          showNotification(`⚠️ ${data.warning}. Replace them with words that share letters.`, 'error');
        } else {
          showNotification(editingLevel ? 'Level updated!' : `Level ${data.level_number} created!`, 'success');
        }
        setEditingLevel(null);
        setLevelWords([{ english: '', darija: '' }]);
        setLevelTitle('');
        setLevelNumber('');
        setLevelIsTraining(false);
        setLevelFreeHints(false);
        setLevelRevealCount(0);
        setPreviewGrid(data.grid_layout);
        loadLevels();
        loadStats();
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Failed to save', 'error');
      }
    } catch (e) {
      showNotification('Failed to save level', 'error');
    }
  };

  // ── Delete level ──────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const deleteLevel = async (levelId) => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/levels/${levelId}`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        showNotification('Level deleted', 'success');
        setDeleteConfirm(null);
        loadLevels();
        loadStats();
      }
    } catch (e) {
      showNotification('Failed to delete', 'error');
    }
  };

  // ── Edit level ────────────────────────────────────────────────────────
  const startEdit = (level) => {
    setEditingLevel(level);
    setLevelWords(level.words || [{ english: '', darija: '' }]);
    setLevelTitle(level.title || '');
    setLevelDifficulty(level.difficulty || 'easy');
    setLevelNumber(level.level_number || '');
    setLevelIsTraining(level.is_training || false);
    setLevelFreeHints(level.free_hints || false);
    setLevelRevealCount(level.reveal_word_count || 0);
    setActiveTab('levels');
  };

  // ── Auto-generate ─────────────────────────────────────────────────────
  const autoGenerate = async () => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/auto-generate`, {
        method: 'POST', headers,
        body: JSON.stringify({ word_count: autoWordCount, difficulty: autoDifficulty })
      });
      if (res.ok) {
        const data = await res.json();
        setAutoPreview(data);
        showNotification(`Generated ${data.words.length} words — review and save`, 'success');
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Failed to generate', 'error');
      }
    } catch (e) {
      showNotification('Failed to auto-generate', 'error');
    }
  };

  const saveAutoGenerated = () => {
    if (!autoPreview) return;
    setLevelWords(autoPreview.words);
    setLevelDifficulty(autoPreview.difficulty);
    setAutoPreview(null);
    setActiveTab('levels');
    showNotification('Words loaded into editor — review and save', 'info');
  };

  // ── Icon picker ──────────────────────────────────────────────────────
  const openIconPicker = (target = 'training') => {
    setIconPickerTarget(target);
    setIconPickerOpen(true);
  };

  const handleIconSelect = (url) => {
    setIconPickerOpen(false);
    if (iconPickerTarget === 'htp') {
      setHtpIcon(url);
    } else if (iconPickerTarget === 'gamecard') {
      setGameCard(prev => ({ ...prev, icon_url: url }));
    } else {
      setSettings(prev => ({ ...prev, training_icon: url }));
    }
  };

  // ── Update settings ───────────────────────────────────────────────────
  const updateSettings = async (updates) => {
    try {
      const res = await fetch(`${API}/admin/word-puzzle/settings`, {
        method: 'PUT', headers, body: JSON.stringify(updates)
      });
      if (res.ok) {
        showNotification('Settings updated', 'success');
        loadSettings();
      }
    } catch (e) {
      showNotification('Failed to update settings', 'error');
    }
  };

  if (loading) {
    return <div className={`p-6 text-center ${text}`}>Loading Word Puzzle admin...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`${bg} rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl border ${border}`}>
            <h3 className={`text-lg font-bold mb-2 ${text}`}>Delete Level?</h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              This will permanently delete Level {deleteConfirm.level_number} and all player progress for it.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className={`flex-1 py-2 rounded-lg border ${border} ${text}`}>
                Cancel
              </button>
              <button onClick={() => deleteLevel(deleteConfirm.id)}
                className="flex-1 py-2 rounded-lg bg-red-500 text-white font-semibold">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-2 flex-wrap">
        {['levels', 'auto-generate', 'known-words', 'how-to-play', 'design', 'settings', 'stats', 'players'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === tab
                ? 'bg-blue-500 text-white'
                : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {tab === 'levels' ? `Levels (${levels.length})` :
             tab === 'auto-generate' ? 'Auto-Generate' :
             tab === 'known-words' ? `Known Words (${knownWords.length})` :
             tab === 'how-to-play' ? `How to Play (${htpSteps.length})` :
             tab === 'design' ? 'Design Puzzle' :
             tab === 'settings' ? 'Settings' : tab === 'stats' ? 'Stats' : 'Players'}
          </button>
        ))}
      </div>

      {/* ── LEVELS TAB ── */}
      {activeTab === 'levels' && (
        <div className="space-y-4">
          {/* Level editor */}
          <div className={`${bg} rounded-xl p-4 border ${border}`}>
            <h3 className={`text-sm font-bold mb-3 ${text}`}>
              {editingLevel ? `Edit Level ${editingLevel.level_number}` : 'Create New Level'}
            </h3>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Title</label>
                <input value={levelTitle} onChange={e => setLevelTitle(e.target.value)}
                  placeholder="e.g. Food & Drinks"
                  className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
              </div>
              <div>
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Difficulty</label>
                <div className="flex gap-1 mt-1">
                  {['easy', 'medium', 'hard'].map(d => (
                    <button key={d} onClick={() => setLevelDifficulty(d)}
                      style={{
                        flex: 1, padding: '6px 0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        border: levelDifficulty === d ? '2px solid' : '1px solid rgba(255,255,255,0.15)',
                        background: levelDifficulty === d
                          ? d === 'easy' ? 'rgba(16,185,129,0.2)' : d === 'medium' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'
                          : 'transparent',
                        borderColor: d === 'easy' ? '#10b981' : d === 'medium' ? '#f59e0b' : '#ef4444',
                        color: d === 'easy' ? '#34d399' : d === 'medium' ? '#fbbf24' : '#f87171',
                        opacity: levelDifficulty === d ? 1 : 0.4
                      }}>
                      {levelDifficulty === d ? '✓ ' : ''}{d.charAt(0).toUpperCase() + d.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Level #</label>
                <input type="number" min="0" value={levelNumber}
                  onChange={e => setLevelNumber(e.target.value)}
                  placeholder="Auto"
                  className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
              </div>
            </div>

            {/* Training & Free Hints toggles */}
            <div className="flex gap-4 mb-3">
              <label className={`flex items-center gap-2 text-xs font-semibold cursor-pointer ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <input type="checkbox"
                  checked={levelIsTraining}
                  onChange={e => setLevelIsTraining(e.target.checked)}
                  className="rounded" />
                🎓 Training Level
              </label>
              <label className={`flex items-center gap-2 text-xs font-semibold cursor-pointer ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <input type="checkbox"
                  checked={levelFreeHints}
                  onChange={e => setLevelFreeHints(e.target.checked)}
                  className="rounded" />
                👁 Free Hints (reveal ALL Darija)
              </label>
              <div className={`flex items-center gap-2 text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                🔢 Reveal
                <input type="number" min="0" max={levelWords.filter(w => w.english.trim()).length || 10}
                  value={levelRevealCount} onChange={e => setLevelRevealCount(Math.max(0, parseInt(e.target.value) || 0))}
                  style={{ width: 48, padding: '2px 6px', borderRadius: 6, textAlign: 'center', fontSize: 12,
                    background: isDarkMode ? '#374151' : '#f3f4f6', border: '1px solid rgba(255,255,255,0.15)' }} />
                word{levelRevealCount !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Word rows */}
            <div className="space-y-2 mb-3">
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Words ({levelWords.length})
              </label>
              {levelWords.map((w, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={w.english} onChange={e => updateWord(i, 'english', e.target.value)}
                    placeholder="English" className={`flex-1 px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
                  <input value={w.darija} onChange={e => updateWord(i, 'darija', e.target.value)}
                    placeholder="Darija (Latin)" className={`flex-1 px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
                  {levelWords.length > 1 && (
                    <button onClick={() => removeWordRow(i)}
                      className="text-red-400 hover:text-red-300 text-lg px-1">×</button>
                  )}
                </div>
              ))}
              <button onClick={addWordRow}
                className={`text-xs font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                + Add word
              </button>
            </div>

            <div className="flex gap-2">
              <button onClick={saveLevel}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold">
                {editingLevel ? 'Update Level' : 'Create Level'}
              </button>
              {editingLevel && (
                <button onClick={() => {
                  setEditingLevel(null);
                  setLevelWords([{ english: '', darija: '' }]);
                  setLevelTitle('');
                  setLevelNumber('');
                  setLevelIsTraining(false);
                  setLevelFreeHints(false);
                  setLevelRevealCount(0);
                }} className={`px-4 py-2 rounded-lg text-sm border ${border} ${text}`}>
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Regenerate grids + level list */}
          <div className="space-y-2">
            {levels.length > 0 && (
              <div className="flex justify-end">
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${API}/admin/word-puzzle/levels/regenerate-grids`, { method: 'POST', headers });
                    if (res.ok) { const d = await res.json(); showNotification(d.message, 'success'); }
                  } catch (e) { showNotification('Error', 'error'); }
                }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                  🔄 Regenerate All Grids
                </button>
              </div>
            )}
            {levels.map((level, idx) => (
              <div key={level.id} className={`${bg} rounded-xl p-3 border ${border} flex items-center gap-3`}>
                {/* Reorder arrows */}
                <div className="flex flex-col gap-0.5">
                  <button onClick={async () => {
                    if (idx === 0) return;
                    const prev = levels[idx - 1];
                    await fetch(`${API}/admin/word-puzzle/levels/${level.id}`, { method: 'PUT', headers, body: JSON.stringify({ level_number: prev.level_number }) });
                    await fetch(`${API}/admin/word-puzzle/levels/${prev.id}`, { method: 'PUT', headers, body: JSON.stringify({ level_number: level.level_number }) });
                    loadLevels();
                  }} className={`text-xs px-1 rounded ${idx === 0 ? 'opacity-20' : 'hover:bg-gray-600 cursor-pointer'} ${text}`}>▲</button>
                  <button onClick={async () => {
                    if (idx === levels.length - 1) return;
                    const next = levels[idx + 1];
                    await fetch(`${API}/admin/word-puzzle/levels/${level.id}`, { method: 'PUT', headers, body: JSON.stringify({ level_number: next.level_number }) });
                    await fetch(`${API}/admin/word-puzzle/levels/${next.id}`, { method: 'PUT', headers, body: JSON.stringify({ level_number: level.level_number }) });
                    loadLevels();
                  }} className={`text-xs px-1 rounded ${idx === levels.length - 1 ? 'opacity-20' : 'hover:bg-gray-600 cursor-pointer'} ${text}`}>▼</button>
                </div>

                {/* Level number badge */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                  level.is_locked ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {level.level_number}
                </div>

                {/* Level info */}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold ${text} flex items-center gap-2`}>
                    {level.title || 'Untitled'}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                      level.difficulty === 'hard' ? 'bg-red-500/20 text-red-400' :
                      level.difficulty === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>{level.difficulty}</span>
                    {level.is_training && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 font-semibold">🎓 TRAINING</span>}
                    {level.free_hints && !level.is_training && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-400 font-semibold">👁 HINTS</span>}
                    {!level.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-400 font-semibold">INACTIVE</span>}
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} truncate`}>
                    {(level.words || []).length} words · {(level.words || []).map(w => w.darija).join(', ')}
                  </div>
                  {level.unplaced_words && level.unplaced_words.length > 0 && (
                    <div className="text-[10px] text-red-400 font-semibold mt-0.5">
                      ⚠️ Not in grid: {level.unplaced_words.join(', ')}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-1.5 items-center flex-shrink-0">
                  <button onClick={async () => {
                    await fetch(`${API}/admin/word-puzzle/levels/${level.id}`, {
                      method: 'PUT', headers, body: JSON.stringify({ is_locked: !level.is_locked })
                    });
                    loadLevels();
                  }} className={`text-sm px-1.5 py-1 rounded-lg ${level.is_locked ? 'text-red-400' : 'text-gray-400'} hover:opacity-80`}
                    title={level.is_locked ? 'Unlock level' : 'Lock level'}>
                    {level.is_locked ? '🔒' : '🔓'}
                  </button>
                  <button onClick={() => startEdit(level)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    Edit
                  </button>
                  <button onClick={() => setDeleteConfirm(level)}
                    className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400">
                    Del
                  </button>
                </div>
              </div>
            ))}
            {levels.length === 0 && (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No levels yet. Create your first level above or use Auto-Generate.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── AUTO-GENERATE TAB ── */}
      {activeTab === 'auto-generate' && (
        <div className={`${bg} rounded-xl p-4 border ${border} space-y-4`}>
          <h3 className={`text-sm font-bold ${text}`}>Auto-Generate Level from Translation Database</h3>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Picks random words from your translations that share letters for good crossword intersections.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Word Count</label>
              <select value={autoWordCount} onChange={e => setAutoWordCount(Number(e.target.value))}
                className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`}>
                {[3, 4, 5, 6, 7, 8].map(n => (
                  <option key={n} value={n}>{n} words</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Difficulty</label>
              <select value={autoDifficulty} onChange={e => setAutoDifficulty(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`}>
                <option value="easy">Easy (2-5 letters)</option>
                <option value="medium">Medium (3-7 letters)</option>
                <option value="hard">Hard (4-10 letters)</option>
              </select>
            </div>
          </div>

          <button onClick={autoGenerate}
            className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold">
            Generate Preview
          </button>

          {autoPreview && (
            <div className={`mt-4 p-3 rounded-xl border ${border} ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h4 className={`text-sm font-bold mb-2 ${text}`}>Preview — {autoPreview.words.length} words</h4>
              <div className="space-y-1 mb-3">
                {autoPreview.words.map((w, i) => (
                  <div key={i} className={`text-sm ${text}`}>
                    <span className="font-semibold">{w.english}</span> → <span className="text-blue-400">{w.darija}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={saveAutoGenerated}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold">
                  Load into Editor
                </button>
                <button onClick={autoGenerate}
                  className={`px-4 py-2 rounded-lg text-sm border ${border} ${text}`}>
                  Regenerate
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── KNOWN WORDS TAB ── */}
      {activeTab === 'known-words' && (
        <div className={`${bg} rounded-xl p-4 border ${border} space-y-3`}>
          <h3 className={`text-sm font-bold ${text}`}>Known Darija Words for Word Basket</h3>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            These words are recognized as bonus words in any level (if they can be spelled from the available letters).
            One word per line. Format: <strong>darija = english</strong> (e.g. ba = father)
          </p>
          <textarea
            value={knownWordsText}
            onChange={e => setKnownWordsText(e.target.value)}
            rows={12}
            placeholder={"ba = father\nma = mother\nomi = my uncle\nkhti = my sister\nahi = my brother\ndar = house\nbit = room"}
            className={`w-full px-3 py-2 rounded-lg text-sm font-mono ${inputBg} border ${border}`}
          />
          <div className="flex gap-3 items-center">
            <button onClick={async () => {
              const lines = knownWordsText.split('\n').filter(l => l.trim());
              const words = lines.map(l => {
                const parts = l.split('=');
                return { darija: (parts[0] || '').trim(), english: (parts[1] || '').trim() };
              }).filter(w => w.darija);
              try {
                const res = await fetch(`${API}/admin/word-puzzle/known-words`, {
                  method: 'PUT', headers, body: JSON.stringify({ words })
                });
                if (res.ok) {
                  showNotification(`Saved ${words.length} known words`, 'success');
                  loadKnownWords();
                }
              } catch (e) { showNotification('Error saving', 'error'); }
            }} className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold">
              Save Known Words
            </button>
            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {knownWordsText.split('\n').filter(l => l.trim()).length} words
            </span>
          </div>

          {/* ── Discover Bonus Words from DB ── */}
          <div className={`border-t ${border} pt-3 mt-3`}>
            <div className="flex gap-3 items-center mb-2">
              <button
                onClick={async () => {
                  setDiscovering(true);
                  try {
                    const res = await fetch(`${API}/admin/word-puzzle/discover-bonus-words`, {
                      method: 'POST', headers
                    });
                    if (res.ok) {
                      const data = await res.json();
                      setDiscoveredWords(data.words || []);
                      setDiscoverLevels(data.levels || []);
                      setDiscoverFilter('all');
                      showNotification(`Found ${data.count} possible bonus words`, 'success');
                    }
                  } catch (e) { showNotification('Error discovering words', 'error'); }
                  setDiscovering(false);
                }}
                disabled={discovering}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {discovering ? 'Scanning...' : 'Discover Possible Words from DB'}
              </button>
              {discoveredWords.length > 0 && (
                <>
                  <select
                    value={discoverFilter}
                    onChange={e => setDiscoverFilter(e.target.value)}
                    className={`px-2 py-1.5 rounded-lg text-xs border ${border} ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}
                  >
                    <option value="all">All Levels ({discoveredWords.length})</option>
                    {discoverLevels.map(l => {
                      const cnt = discoveredWords.filter(w => w.levels.some(lv => lv.num === l.num)).length;
                      return cnt > 0 ? (
                        <option key={l.num} value={l.num}>L{l.num}: {l.title} ({cnt})</option>
                      ) : null;
                    })}
                  </select>
                  <button
                    onClick={() => {
                      const filtered = discoverFilter === 'all'
                        ? discoveredWords
                        : discoveredWords.filter(w => w.levels.some(l => l.num === parseInt(discoverFilter)));
                      const newLines = filtered.map(w => `${w.darija} = ${w.english}`).join('\n');
                      setKnownWordsText(prev => prev ? prev + '\n' + newLines : newLines);
                      showNotification(`Added ${filtered.length} words to list`, 'success');
                      setDiscoveredWords(prev => prev.filter(w => !filtered.includes(w)));
                    }}
                    className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-semibold"
                  >
                    Add All ({discoverFilter === 'all'
                      ? discoveredWords.length
                      : discoveredWords.filter(w => w.levels.some(l => l.num === parseInt(discoverFilter))).length})
                  </button>
                </>
              )}
            </div>
            {discoveredWords.length > 0 && (() => {
              const filtered = discoverFilter === 'all'
                ? discoveredWords
                : discoveredWords.filter(w => w.levels.some(l => l.num === parseInt(discoverFilter)));
              return (
              <div className={`rounded-lg border ${border} max-h-60 overflow-y-auto`}>
                {filtered.map((w, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-1.5 text-xs ${i % 2 === 0 ? (isDarkMode ? 'bg-gray-900' : 'bg-gray-50') : ''}`}>
                    <span className={text}>
                      <strong>{w.darija}</strong> = {w.english}
                      <span className={`ml-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        ({w.levels.map(l => l.title || `L${l.num}`).join(', ')})
                      </span>
                    </span>
                    <button
                      onClick={() => {
                        setKnownWordsText(prev => prev ? prev + '\n' + `${w.darija} = ${w.english}` : `${w.darija} = ${w.english}`);
                        setDiscoveredWords(prev => prev.filter(dw => dw.darija !== w.darija));
                      }}
                      className="px-2 py-0.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-semibold ml-2 flex-shrink-0"
                    >
                      + Add
                    </button>
                  </div>
                ))}
              </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── HOW TO PLAY TAB ── */}
      {activeTab === 'how-to-play' && (
        <div className="space-y-4">
          {/* Editor */}
          <div className={`${bg} rounded-xl p-4 border ${border}`}>
            <h3 className={`text-sm font-bold mb-3 ${text}`}>
              {htpEditing ? 'Edit Step' : 'Add New Step'}
            </h3>
            <div className="flex items-end gap-3 mb-3">
              <div>
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Icon</label>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                    {htpIcon && htpIcon.startsWith('/')
                      ? <img src={`${API.replace('/api', '')}${htpIcon}`} alt="" className="w-8 h-8 object-contain" />
                      : <span className="text-xl">{htpIcon || '📝'}</span>}
                  </div>
                  <button onClick={() => openIconPicker('htp')}
                    className={`px-2 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>
                    Pick
                  </button>
                  {htpIcon && htpIcon.startsWith('/') && (
                    <button onClick={() => setHtpIcon('📝')}
                      className={`px-2 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-red-700/50 text-red-300' : 'bg-red-100 text-red-600'}`}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Title</label>
                <input value={htpTitle} onChange={e => setHtpTitle(e.target.value)}
                  placeholder="Step title"
                  className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
              </div>
            </div>
            <div className="mb-3">
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
              <textarea value={htpDesc} onChange={e => setHtpDesc(e.target.value)}
                placeholder="Explain this step to the player..."
                rows={3}
                className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
            </div>
            <div className="flex gap-2">
              <button onClick={async () => {
                if (!htpTitle.trim() || !htpDesc.trim()) {
                  showNotification('Title and description required', 'error');
                  return;
                }
                try {
                  const url = htpEditing
                    ? `${API}/admin/word-puzzle/how-to-play/${htpEditing.id}`
                    : `${API}/admin/word-puzzle/how-to-play`;
                  const method = htpEditing ? 'PUT' : 'POST';
                  const res = await fetch(url, {
                    method, headers,
                    body: JSON.stringify({ icon: htpIcon, title: htpTitle.trim(), description: htpDesc.trim() })
                  });
                  if (res.ok) {
                    showNotification(htpEditing ? 'Step updated' : 'Step added', 'success');
                    setHtpEditing(null);
                    setHtpIcon('📝');
                    setHtpTitle('');
                    setHtpDesc('');
                    loadHowToPlay();
                  } else {
                    const err = await res.json();
                    showNotification(err.detail || 'Failed', 'error');
                  }
                } catch (e) {
                  showNotification('Failed to save step', 'error');
                }
              }}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold">
                {htpEditing ? 'Update Step' : 'Add Step'}
              </button>
              {htpEditing && (
                <button onClick={() => {
                  setHtpEditing(null);
                  setHtpIcon('📝');
                  setHtpTitle('');
                  setHtpDesc('');
                }} className={`px-4 py-2 rounded-lg text-sm border ${border} ${text}`}>
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Steps list */}
          <div className="space-y-2">
            {htpSteps.length === 0 && (
              <div className={`text-center py-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <p className="mb-3">No steps yet.</p>
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${API}/admin/word-puzzle/how-to-play/seed`, { method: 'POST', headers });
                    if (res.ok) { showNotification('Default steps seeded!', 'success'); loadHowToPlay(); }
                    else { showNotification('Failed to seed steps', 'error'); }
                  } catch (e) { showNotification('Error: ' + e.message, 'error'); }
                }} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-semibold">
                  Seed Default Steps
                </button>
              </div>
            )}
            {htpSteps.map((step, i) => (
              <div key={step.id} className={`${bg} rounded-xl p-3 border ${border} flex items-start gap-3`}>
                {step.icon && step.icon.startsWith('/')
                  ? <img src={`${API.replace('/api', '')}${step.icon}`} alt="" className="w-6 h-6 object-contain flex-shrink-0 mt-1" />
                  : <span className="text-xl flex-shrink-0 mt-1">{step.icon}</span>}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold ${text}`}>{step.title}</div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{step.description}</div>
                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>Order: {step.order || i + 1}</div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => {
                    setHtpEditing(step);
                    setHtpIcon(step.icon || '📝');
                    setHtpTitle(step.title || '');
                    setHtpDesc(step.description || '');
                  }}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    Edit
                  </button>
                  <button onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/word-puzzle/how-to-play/${step.id}`, {
                        method: 'DELETE', headers
                      });
                      if (res.ok) {
                        showNotification('Step deleted', 'success');
                        loadHowToPlay();
                      }
                    } catch (e) {
                      showNotification('Failed to delete', 'error');
                    }
                  }}
                    className="px-3 py-1 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── DESIGN PUZZLE TAB ── */}
      {activeTab === 'design' && (
        <div className="space-y-6">
          {/* Game Card Settings */}
          <div className={`${bg} rounded-xl p-4 border ${border}`}>
            <h3 className={`text-sm font-bold mb-3 ${text}`}>Game Card (shown on Games page)</h3>

            {/* Live Preview */}
            <div
              className={`rounded-xl p-4 mb-4 text-center ${!gameCard.card_gradient ? (isDarkMode ? 'bg-gray-900' : 'bg-gray-50') : ''} border ${border}`}
              style={gameCard.card_gradient ? { background: gameCard.card_gradient } : {}}>
              <div className="text-5xl mb-2">
                {gameCard.icon_url
                  ? <img src={`${API.replace('/api', '')}${gameCard.icon_url}`} alt="" className="w-16 h-16 object-contain mx-auto" />
                  : gameCard.icon}
              </div>
              <div className={`text-lg font-bold mb-1 ${gameCard.card_gradient ? 'text-white' : text}`}>{gameCard.name}</div>
              <div className={`text-xs ${gameCard.card_gradient ? 'text-gray-200' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{gameCard.description}</div>
              <div className="flex gap-2 justify-center mt-2">
                {gameCard.is_pro && <span className="bg-yellow-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">PRO</span>}
                {!gameCard.is_visible && <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">HIDDEN</span>}
                {gameCard.is_visible && !gameCard.is_pro && <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">FREE</span>}
              </div>
            </div>

            {/* Edit Fields */}
            <div className="space-y-3">
              <div className="flex items-end gap-3">
                <div>
                  <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Icon</label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                      {gameCard.icon_url
                        ? <img src={`${API.replace('/api', '')}${gameCard.icon_url}`} alt="" className="w-10 h-10 object-contain" />
                        : <span className="text-2xl">{gameCard.icon}</span>}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => openIconPicker('gamecard')}
                        className={`px-2 py-1 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>
                        Pick
                      </button>
                      {gameCard.icon_url && (
                        <button onClick={() => setGameCard(prev => ({ ...prev, icon_url: '' }))}
                          className={`px-2 py-1 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-red-700/50 text-red-300' : 'bg-red-100 text-red-600'}`}>
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name</label>
                  <input value={gameCard.name}
                    onChange={e => setGameCard(prev => ({ ...prev, name: e.target.value }))}
                    className={`w-full px-3 py-1.5 rounded-lg text-sm ${inputBg} border ${border}`} />
                </div>
              </div>

              <div>
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                <textarea value={gameCard.description}
                  onChange={e => setGameCard(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className={`w-full px-3 py-1.5 rounded-lg text-sm ${inputBg} border ${border}`} />
              </div>

              <div>
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Card Gradient (CSS)</label>
                <input value={gameCard.card_gradient}
                  onChange={e => setGameCard(prev => ({ ...prev, card_gradient: e.target.value }))}
                  placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  className={`w-full px-3 py-1.5 rounded-lg text-xs font-mono ${inputBg} border ${border}`} />
                <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Leave empty for default card style</span>
              </div>

              <div className="flex gap-4 items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={gameCard.is_visible}
                    onChange={e => setGameCard(prev => ({ ...prev, is_visible: e.target.checked }))}
                    className="w-4 h-4 rounded" />
                  <span className={`text-sm font-semibold ${text}`}>Visible</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={gameCard.is_pro}
                    onChange={e => setGameCard(prev => ({ ...prev, is_pro: e.target.checked }))}
                    className="w-4 h-4 rounded" />
                  <span className={`text-sm font-semibold ${text}`}>Pro Only</span>
                </label>
              </div>

              <button
                disabled={gcSaving}
                onClick={async () => {
                  setGcSaving(true);
                  try {
                    const res = await fetch(`${API}/admin/word-puzzle/game-settings`, {
                      method: 'PUT', headers, body: JSON.stringify(gameCard)
                    });
                    if (res.ok) showNotification('Game card updated!', 'success');
                    else showNotification('Failed to update', 'error');
                  } catch (e) { showNotification('Error saving', 'error'); }
                  finally { setGcSaving(false); }
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                {gcSaving ? 'Saving...' : 'Save Game Card'}
              </button>
            </div>
          </div>

          {/* Backgrounds Management */}
          <div className={`${bg} rounded-xl p-4 border ${border}`}>
            <div className="flex justify-between items-center mb-3">
              <h3 className={`text-sm font-bold ${text}`}>Backgrounds ({backgrounds.length})</h3>
              <div className="flex gap-2">
                {backgrounds.length === 0 && (
                  <button onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/word-puzzle/backgrounds/seed`, { method: 'POST', headers });
                      if (res.ok) { showNotification('Default gradients seeded!', 'success'); loadBackgrounds(); }
                    } catch (e) { showNotification('Error seeding', 'error'); }
                  }} className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                    Seed Defaults
                  </button>
                )}
              </div>
            </div>

            {/* Existing backgrounds grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
              {backgrounds.map((b, bi) => (
                <div key={b.id} className={`rounded-lg border ${border} overflow-hidden`}>
                  <div style={{
                    height: 60,
                    backgroundColor: '#1a1a2e',
                    background: b.type === 'image' ? `#1a1a2e url(${API.replace('/api', '')}${b.value}) center/contain no-repeat` : b.value
                  }} />
                  <div className="p-2">
                    <div className="flex items-center gap-1">
                      <span className={`text-xs font-semibold truncate flex-1 ${text}`}>{b.name}</span>
                      {b.is_default && <span className="text-yellow-500 text-xs">★</span>}
                      {b.is_locked && <span className="text-xs">🔒</span>}
                    </div>
                    <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{b.type} · {b.price ? `🪙${b.price}` : 'Free'}</span>
                    <div className="flex gap-1 mt-1">
                      {bi > 0 && <button onClick={() => swapOrder('backgrounds', backgrounds, bi, -1, loadBackgrounds)}
                        className="text-gray-400 hover:text-white text-[10px]" title="Move up">▲</button>}
                      {bi < backgrounds.length - 1 && <button onClick={() => swapOrder('backgrounds', backgrounds, bi, 1, loadBackgrounds)}
                        className="text-gray-400 hover:text-white text-[10px]" title="Move down">▼</button>}
                      <button onClick={async () => {
                        try {
                          await fetch(`${API}/admin/word-puzzle/backgrounds/${b.id}`, {
                            method: 'PUT', headers, body: JSON.stringify({ is_locked: !b.is_locked })
                          });
                          showNotification(b.is_locked ? 'Unlocked' : 'Locked', 'success');
                          loadBackgrounds();
                        } catch (e) { showNotification('Error', 'error'); }
                      }} className={`text-[10px] hover:underline ${b.is_locked ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {b.is_locked ? '🔒' : '🔓'}
                      </button>
                      <button onClick={() => {
                        setBgEditing(b);
                        setBgName(b.name);
                        setBgType(b.type);
                        setBgValue(b.value);
                        setBgIsDefault(b.is_default || false);
                        setBgIsLocked(b.is_locked || false);
                        setBgPrice(b.price || 0);
                      }} className="text-blue-400 text-[10px] hover:underline">Edit</button>
                      {!b.is_locked && <button onClick={async () => {
                        try {
                          const res = await fetch(`${API}/admin/word-puzzle/backgrounds/${b.id}`, { method: 'DELETE', headers });
                          if (res.ok) { showNotification('Deleted', 'success'); loadBackgrounds(); }
                          else { const err = await res.json().catch(() => ({})); showNotification(err.detail || 'Error', 'error'); }
                        } catch (e) { showNotification('Error', 'error'); }
                      }} className="text-red-400 text-[10px] hover:underline">Del</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add/Edit form */}
            <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} border ${border}`}>
              <h4 className={`text-xs font-bold mb-2 ${text}`}>{bgEditing ? 'Edit Background' : 'Add Background'}</h4>
              <div className="space-y-2">
                <input value={bgName} onChange={e => setBgName(e.target.value)}
                  placeholder="Name (e.g. Desert Sunset)"
                  className={`w-full px-3 py-1.5 rounded-lg text-sm ${inputBg} border ${border}`} />

                <div className="flex gap-2">
                  <button onClick={() => setBgType('gradient')}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold ${bgType === 'gradient' ? 'bg-blue-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                    Gradient
                  </button>
                  <button onClick={() => setBgType('image')}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold ${bgType === 'image' ? 'bg-blue-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                    Image
                  </button>
                </div>

                {bgType === 'gradient' ? (
                  <textarea value={bgValue} onChange={e => {
                    let v = e.target.value;
                    // Auto-strip CSS property prefix for real-time preview
                    for (const pfx of ['background-image:', 'background:']) {
                      if (v.trim().toLowerCase().startsWith(pfx)) {
                        v = v.trim().slice(pfx.length).trim().replace(/;$/, '').trim();
                      }
                    }
                    setBgValue(v);
                  }}
                    placeholder="linear-gradient(180deg, #000 0%, #333 100%)"
                    rows={2}
                    className={`w-full px-3 py-1.5 rounded-lg text-xs font-mono ${inputBg} border ${border}`} />
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                      <input type="file" accept="image/png,image/jpeg,image/webp"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setBgUploading(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await fetch(`${API}/admin/word-puzzle/backgrounds/upload-image`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${adminToken}` },
                              body: formData
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setBgValue(data.url);
                              showNotification('Image uploaded!', 'success');
                            } else showNotification('Upload failed', 'error');
                          } catch (err) { showNotification('Upload error', 'error'); }
                          finally { setBgUploading(false); }
                        }}
                        className={`text-xs ${text}`} />
                      {bgUploading && <span className="text-xs text-blue-400">Uploading...</span>}
                    </div>
                    <button onClick={() => { loadPickerImages(); setShowImagePicker(true); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white`}>
                      📁 Pick from Library
                    </button>
                  </div>
                )}

                {bgValue && (
                  <div style={{
                    height: 60, borderRadius: 8,
                    backgroundColor: '#1a1a2e',
                    background: bgType === 'image' ? `#1a1a2e url(${API.replace('/api', '')}${bgValue}) center/contain no-repeat` : bgValue
                  }} />
                )}

                <div>
                  <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Price (coins, 0 = free)</label>
                  <input type="number" min="0" step="50" value={bgPrice}
                    onChange={e => setBgPrice(parseInt(e.target.value) || 0)}
                    className={`w-full px-3 py-1.5 rounded-lg text-sm ${inputBg} border ${border}`} />
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={bgIsDefault}
                    onChange={e => setBgIsDefault(e.target.checked)}
                    className="w-4 h-4 rounded" />
                  <span className={`text-xs font-semibold ${text}`}>Set as Default</span>
                </label>

                <div className="flex gap-2">
                  <button onClick={async () => {
                    if (!bgName || !bgValue) { showNotification('Name and value required', 'error'); return; }
                    try {
                      const body = { name: bgName, type: bgType, value: bgValue, is_default: bgIsDefault, is_locked: bgIsLocked, price: bgPrice };
                      const url = bgEditing
                        ? `${API}/admin/word-puzzle/backgrounds/${bgEditing.id}`
                        : `${API}/admin/word-puzzle/backgrounds`;
                      const method = bgEditing ? 'PUT' : 'POST';
                      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
                      if (res.ok) {
                        showNotification(bgEditing ? 'Updated!' : 'Created!', 'success');
                        loadBackgrounds();
                        setBgEditing(null); setBgName(''); setBgValue(''); setBgIsDefault(false); setBgIsLocked(false); setBgPrice(0);
                      } else {
                        const err = await res.json().catch(() => ({}));
                        showNotification(err.detail || `Failed to ${bgEditing ? 'update' : 'create'} background`, 'error');
                      }
                    } catch (e) { showNotification('Error saving: ' + e.message, 'error'); }
                  }} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold">
                    {bgEditing ? 'Update' : 'Add Background'}
                  </button>
                  {bgEditing && (
                    <button onClick={() => { setBgEditing(null); setBgName(''); setBgValue(''); setBgIsDefault(false); setBgIsLocked(false); setBgPrice(0); }}
                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Image Blur Control */}
          <div className={`${bg} rounded-xl p-4 border ${border}`}>
            <h3 className={`text-sm font-bold mb-3 ${text}`}>Image Background Blur</h3>
            <div className="flex items-center gap-3">
              <input type="range" min="0" max="20" step="1"
                value={settings?.bg_blur || 0}
                onChange={e => setSettings(prev => ({ ...prev, bg_blur: parseInt(e.target.value) }))}
                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: '#3b82f6' }} />
              <span className={`text-sm font-mono font-bold w-12 text-center ${text}`}>{settings?.bg_blur || 0}px</span>
            </div>
            <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Adds blur to uploaded image backgrounds. 0 = no blur. Higher = more blur (puzzle stands out more).
            </p>
            <button onClick={async () => {
              try {
                const res = await fetch(`${API}/admin/word-puzzle/settings`, {
                  method: 'PUT', headers, body: JSON.stringify({ bg_blur: settings?.bg_blur || 0 })
                });
                if (res.ok) showNotification('Blur setting saved!', 'success');
                else showNotification('Failed to save', 'error');
              } catch (e) { showNotification('Error: ' + e.message, 'error'); }
            }} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold mt-2">
              Save Blur
            </button>
          </div>

          {/* Hex Styles Management */}
          <div className={`${bg} rounded-xl p-4 border ${border}`}>
            <div className="flex justify-between items-center mb-3">
              <h3 className={`text-sm font-bold ${text}`}>Honeycomb Colors ({hexStyles.length})</h3>
            </div>

            {/* Existing hex styles grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
              {hexStyles.map((h, hi) => (
                <div key={h.id} className={`rounded-lg border ${border} overflow-hidden`}>
                  <div style={{
                    height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: h.hex_bg || '#f0f0f0'
                  }}>
                    <div style={{
                      width: 36, height: 36,
                      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                      background: h.hex_bg || '#fff',
                      border: `2px solid ${h.hex_border || '#ccc'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700, color: h.hex_text || '#1e293b'
                    }}>A</div>
                  </div>
                  <div className="p-1.5">
                    <div className={`text-xs font-semibold text-center ${text}`}>{h.name}</div>
                    <div className="text-center text-[10px] text-gray-400">{h.price ? `🪙${h.price}` : 'Free'}</div>
                    <div className="flex gap-1 justify-center mt-1">
                      {hi > 0 && <button onClick={() => swapOrder('hex-styles', hexStyles, hi, -1, loadHexStyles)}
                        className="text-gray-400 hover:text-white text-[10px]" title="Move up">▲</button>}
                      {hi < hexStyles.length - 1 && <button onClick={() => swapOrder('hex-styles', hexStyles, hi, 1, loadHexStyles)}
                        className="text-gray-400 hover:text-white text-[10px]" title="Move down">▼</button>}
                      <button onClick={() => {
                        setHsEditing(h);
                        setHsName(h.name);
                        setHsBg(h.hex_bg || '');
                        setHsBorder(h.hex_border || '');
                        setHsText(h.hex_text || '#1e293b');
                        setHsPrice(h.price || 0);
                      }} className="text-blue-400 text-[10px] hover:underline">Edit</button>
                      <button onClick={() => setShopDeleteConfirm({ id: h.id, name: h.name, type: 'hex-style' })}
                        className="text-red-400 text-[10px] hover:underline">Del</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add/Edit hex style form */}
            <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} border ${border}`}>
              <h4 className={`text-xs font-bold mb-2 ${text}`}>{hsEditing ? 'Edit Hex Style' : 'Add Hex Style'}</h4>
              <div className="space-y-2">
                <input value={hsName} onChange={e => setHsName(e.target.value)}
                  placeholder="Name (e.g. Gold)"
                  className={`w-full px-3 py-1.5 rounded-lg text-sm ${inputBg} border ${border}`} />

                <div>
                  <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hex Background (CSS gradient)</label>
                  <textarea value={hsBg} onChange={e => setHsBg(e.target.value)}
                    placeholder="linear-gradient(180deg, rgba(251,191,36,0.95), rgba(217,119,6,0.9))"
                    rows={2}
                    className={`w-full px-3 py-1.5 rounded-lg text-xs font-mono ${inputBg} border ${border}`} />
                </div>

                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Border Color</label>
                    <input value={hsBorder} onChange={e => setHsBorder(e.target.value)}
                      placeholder="rgba(251,191,36,0.6)"
                      className={`w-full px-3 py-1.5 rounded-lg text-xs font-mono ${inputBg} border ${border}`} />
                  </div>
                  <div className="w-28">
                    <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Text Color</label>
                    <input value={hsText} onChange={e => setHsText(e.target.value)}
                      placeholder="#7c2d12"
                      className={`w-full px-3 py-1.5 rounded-lg text-xs font-mono ${inputBg} border ${border}`} />
                  </div>
                </div>

                <div>
                  <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Price (coins, 0 = free)</label>
                  <input type="number" min="0" step="50" value={hsPrice}
                    onChange={e => setHsPrice(parseInt(e.target.value) || 0)}
                    className={`w-full px-3 py-1.5 rounded-lg text-sm ${inputBg} border ${border}`} />
                </div>

                {/* Live preview */}
                {hsBg && (
                  <div className="flex items-center gap-3">
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preview:</span>
                    <div style={{
                      width: 44, height: 44,
                      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                      background: hsBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, fontWeight: 800, color: hsText || '#1e293b'
                    }}>A</div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button onClick={async () => {
                    if (!hsName || !hsBg) { showNotification('Name and background required', 'error'); return; }
                    try {
                      const body = { name: hsName, hex_bg: hsBg, hex_border: hsBorder, hex_text: hsText, price: hsPrice };
                      const url = hsEditing
                        ? `${API}/admin/word-puzzle/hex-styles/${hsEditing.id}`
                        : `${API}/admin/word-puzzle/hex-styles`;
                      const method = hsEditing ? 'PUT' : 'POST';
                      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
                      if (res.ok) {
                        showNotification(hsEditing ? 'Updated!' : 'Created!', 'success');
                        loadHexStyles();
                        setHsEditing(null); setHsName(''); setHsBg(''); setHsBorder(''); setHsText('#1e293b'); setHsPrice(200);
                      } else {
                        const err = await res.json().catch(() => ({}));
                        showNotification(err.detail || 'Failed', 'error');
                      }
                    } catch (e) { showNotification('Error: ' + e.message, 'error'); }
                  }} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold">
                    {hsEditing ? 'Update' : 'Add Hex Style'}
                  </button>
                  {hsEditing && (
                    <button onClick={() => { setHsEditing(null); setHsName(''); setHsBg(''); setHsBorder(''); setHsText('#1e293b'); setHsPrice(200); }}
                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS TAB ── */}
      {activeTab === 'settings' && settings && (
        <div className={`${bg} rounded-xl p-4 border ${border} space-y-4`}>
          <h3 className={`text-sm font-bold ${text}`}>Game Settings</h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Letter Hint Cost
              </label>
              <input type="number" value={settings.hint_costs?.letter || 60}
                onChange={e => setSettings(prev => ({
                  ...prev, hint_costs: { ...prev.hint_costs, letter: Number(e.target.value) }
                }))}
                className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
            </div>
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Word Hint Cost
              </label>
              <input type="number" value={settings.hint_costs?.word || 120}
                onChange={e => setSettings(prev => ({
                  ...prev, hint_costs: { ...prev.hint_costs, word: Number(e.target.value) }
                }))}
                className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
            </div>
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Bonus Basket Reward (coins)
              </label>
              <input type="number" value={settings.bonus_basket_reward || 30}
                onChange={e => setSettings(prev => ({ ...prev, bonus_basket_reward: Number(e.target.value) }))}
                className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
            </div>
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Starting Coins (new players)
              </label>
              <input type="number" value={settings.starting_coins || 50}
                onChange={e => setSettings(prev => ({ ...prev, starting_coins: Number(e.target.value) }))}
                className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
            </div>
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Free Levels (always unlocked)
              </label>
              <input type="number" min="1" value={settings.free_levels || 1}
                onChange={e => setSettings(prev => ({ ...prev, free_levels: Number(e.target.value) }))}
                className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
              <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                First N levels are always unlocked for all users, no progression needed.
              </p>
            </div>
          </div>

          <div>
            <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Honeycomb & Grid Design
            </label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <div>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hex Size (px)</span>
                <input type="number" min="20" max="60" value={settings.hex_size || 34}
                  onChange={e => setSettings(prev => ({ ...prev, hex_size: Number(e.target.value) }))}
                  className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
              </div>
              <div>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hex Gap (px)</span>
                <input type="number" min="2" max="20" value={settings.hex_gap || 10}
                  onChange={e => setSettings(prev => ({ ...prev, hex_gap: Number(e.target.value) }))}
                  className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
              </div>
              <div>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Grid Cell Max (px)</span>
                <input type="number" min="20" max="60" value={settings.grid_cell_max || 34}
                  onChange={e => setSettings(prev => ({ ...prev, grid_cell_max: Number(e.target.value) }))}
                  className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
              </div>
            </div>
            <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Hex Size = alphabet button size, Hex Gap = space between them, Grid Cell Max = crossword cell cap.
            </p>
          </div>

          <div>
            <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Grid Cell Opacity (glassy look)
            </label>
            <div className="flex items-center gap-3 mt-1">
              <input type="range" min="5" max="80" step="1"
                value={settings.grid_opacity ?? 25}
                onChange={e => setSettings(prev => ({ ...prev, grid_opacity: parseInt(e.target.value) }))}
                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: '#3b82f6' }} />
              <span className={`text-sm font-mono font-bold w-12 text-center ${text}`}>{settings.grid_opacity ?? 25}%</span>
            </div>
            <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Opacity of unfilled crossword cells. Lower = more transparent (glassy), higher = more solid.
            </p>
          </div>

          <div>
            <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Grid Cell Color
            </label>
            <div className="flex items-center gap-3 mt-1">
              <input type="color"
                value={settings.grid_color || '#C8B4DC'}
                onChange={e => setSettings(prev => ({ ...prev, grid_color: e.target.value }))}
                style={{ width: 40, height: 30, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
              <span className={`text-xs font-mono ${text}`}>{settings.grid_color || '#C8B4DC'}</span>
              <button onClick={() => updateSettings({ grid_color: settings.grid_color })}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold">
                Save
              </button>
            </div>
            <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Color of unfilled crossword cells. Combined with opacity above.
            </p>
          </div>

          <div>
            <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Level Completion Rewards
            </label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {['easy', 'medium', 'hard'].map(diff => (
                <div key={diff}>
                  <span className={`text-xs capitalize ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{diff}</span>
                  <input type="number" value={settings.level_rewards?.[diff] || 20}
                    onChange={e => setSettings(prev => ({
                      ...prev, level_rewards: { ...prev.level_rewards, [diff]: Number(e.target.value) }
                    }))}
                    className={`w-full px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Training Instruction (Level Select Screen)
              </label>
              <button
                onClick={() => setSettings(prev => ({ ...prev, training_visible: !prev.training_visible }))}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  settings.training_visible !== false
                    ? (isDarkMode ? 'bg-green-700/50 text-green-300' : 'bg-green-100 text-green-700')
                    : (isDarkMode ? 'bg-red-700/50 text-red-300' : 'bg-red-100 text-red-600')
                }`}>
                {settings.training_visible !== false ? '👁 Visible' : '🚫 Hidden'}
              </button>
            </div>
            <div className="flex items-center gap-3 mt-2 mb-2">
              <div className={`w-12 h-12 flex items-center justify-center rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                {settings.training_icon
                  ? <img src={`${API.replace('/api', '')}${settings.training_icon}`} alt="" className="w-10 h-10 object-contain" />
                  : <span className="text-2xl">📖</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => openIconPicker('training')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>
                  Pick Icon
                </button>
                {settings.training_icon && (
                  <button onClick={() => setSettings(prev => ({ ...prev, training_icon: '' }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-red-700/50 hover:bg-red-600/50 text-red-300' : 'bg-red-100 hover:bg-red-200 text-red-600'}`}>
                    Clear
                  </button>
                )}
              </div>
            </div>
            <input type="text" value={settings.training_title || ''}
              onChange={e => setSettings(prev => ({ ...prev, training_title: e.target.value }))}
              placeholder="Start with Level 1 (Training)"
              className={`w-full px-3 py-2 rounded-lg text-sm mt-1 ${inputBg} border ${border}`} />
            <textarea value={settings.training_description || ''}
              onChange={e => setSettings(prev => ({ ...prev, training_description: e.target.value }))}
              placeholder="Learn how the puzzle works before playing! We use Latin script (abc, not Arabic)."
              rows={2}
              className={`w-full px-3 py-2 rounded-lg text-sm mt-1 ${inputBg} border ${border}`} />
            <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Leave empty to use defaults. Shows on level select screen when Level 1 is not completed.
            </p>
          </div>

          <button onClick={() => updateSettings(settings)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold">
            Save Settings
          </button>
        </div>
      )}

      {/* ── STATS TAB ── */}
      {activeTab === 'stats' && stats && (
        <div className={`${bg} rounded-xl p-4 border ${border}`}>
          <h3 className={`text-sm font-bold mb-3 ${text}`}>Game Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: 'Total Levels', value: stats.total_levels, icon: '🧩' },
              { label: 'Active Levels', value: stats.active_levels, icon: '✅' },
              { label: 'Total Players', value: stats.total_players, icon: '👥' },
              { label: 'Total Completions', value: stats.total_completions, icon: '🏆' },
              { label: 'Bonus Words Found', value: stats.total_bonus_words_found, icon: '🫙' },
              { label: 'Avg Completions/Player', value: stats.avg_completions, icon: '📊' },
            ].map((stat, i) => (
              <div key={i} className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                <div className="text-xl mb-1">{stat.icon}</div>
                <div className={`text-lg font-bold ${text}`}>{stat.value}</div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* ── PLAYERS TAB ── */}
      {activeTab === 'players' && (
        <div className="space-y-4">
          {/* Search */}
          <div className={`${bg} rounded-xl p-4 border ${border}`}>
            <h3 className={`text-sm font-bold mb-3 ${text}`}>Puzzle Players</h3>
            <div className="flex gap-2">
              <input value={playerSearch} onChange={e => setPlayerSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') loadPlayers(playerSearch, 1); }}
                placeholder="Filter by email or username..."
                className={`flex-1 px-3 py-2 rounded-lg text-sm ${inputBg} border ${border}`} />
              <button onClick={() => loadPlayers(playerSearch, 1)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                {playerLoading ? '...' : 'Search'}
              </button>
              {playerSearch && (
                <button onClick={() => { setPlayerSearch(''); loadPlayers('', 1); }}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                  Clear
                </button>
              )}
            </div>

            {/* Results */}
            {playerLoading && playerResults.length === 0 && (
              <div className={`mt-3 text-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading players...</div>
            )}
            {playerResults.length > 0 && (
              <div className="mt-3 space-y-1">
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{playerTotal} player{playerTotal !== 1 ? 's' : ''}</span>
                {playerResults.map(u => (
                  <div key={u.id} className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-semibold ${text}`}>{u.email}</span>
                      {u.username && <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>({u.username})</span>}
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        🪙 {u.coins ?? 0} coins · Level {u.level ?? 1}
                      </div>
                    </div>
                    <button onClick={async () => {
                      try {
                        const res = await fetch(`${API}/admin/word-puzzle/players/${u.id}/progress`, { headers });
                        if (res.ok) {
                          const d = await res.json();
                          setSelectedPlayerInfo(d.user);
                          setSelectedPlayer(d.progress);
                        }
                      } catch (e) { showNotification('Error loading player', 'error'); }
                    }} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-semibold">
                      View
                    </button>
                  </div>
                ))}
                {/* Pagination */}
                {playerPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <button disabled={playerPage <= 1} onClick={() => loadPlayers(playerSearch, playerPage - 1)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold ${playerPage <= 1 ? 'opacity-40' : ''} ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                      Prev
                    </button>
                    <span className={`text-xs ${text}`}>{playerPage} / {playerPages}</span>
                    <button disabled={playerPage >= playerPages} onClick={() => loadPlayers(playerSearch, playerPage + 1)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold ${playerPage >= playerPages ? 'opacity-40' : ''} ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Player Details */}
          {selectedPlayer && selectedPlayerInfo && (
            <div className={`${bg} rounded-xl p-4 border ${border}`}>
              <div className="flex justify-between items-center mb-3">
                <h3 className={`text-sm font-bold ${text}`}>
                  {selectedPlayerInfo.email} {selectedPlayerInfo.username && `(${selectedPlayerInfo.username})`}
                </h3>
                <button onClick={() => { setSelectedPlayer(null); setSelectedPlayerInfo(null); }}
                  className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} hover:underline`}>Close</button>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Coins', value: selectedPlayer.coins, icon: '🪙' },
                  { label: 'Level', value: selectedPlayer.current_level, icon: '📊' },
                  { label: 'Completed', value: (selectedPlayer.completed_levels || []).length, icon: '✅' },
                  { label: 'Bonus Words', value: selectedPlayer.total_bonus_words, icon: '🏆' },
                ].map((s, i) => (
                  <div key={i} className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="text-lg">{s.icon}</div>
                    <div className={`text-lg font-bold ${text}`}>{s.value}</div>
                    <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Unlocked items */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className={`text-xs font-semibold mb-1 ${text}`}>Backgrounds ({(selectedPlayer.unlocked_backgrounds || []).length})</div>
                  <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {(selectedPlayer.unlocked_backgrounds || []).length === 0 ? 'None' : (selectedPlayer.unlocked_backgrounds || []).join(', ')}
                  </div>
                </div>
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className={`text-xs font-semibold mb-1 ${text}`}>Hex Styles ({(selectedPlayer.unlocked_hex_styles || []).length})</div>
                  <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {(selectedPlayer.unlocked_hex_styles || []).length === 0 ? 'None' : (selectedPlayer.unlocked_hex_styles || []).join(', ')}
                  </div>
                </div>
              </div>

              {/* Quick actions */}
              <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} border ${border}`}>
                <h4 className={`text-xs font-bold mb-2 ${text}`}>Quick Actions</h4>

                {/* Add coins */}
                <div className="flex gap-2 mb-2 flex-wrap">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} self-center`}>Add Coins:</span>
                  {[50, 100, 200, 500].map(amount => (
                    <button key={amount} onClick={async () => {
                      try {
                        const res = await fetch(`${API}/admin/word-puzzle/players/${selectedPlayerInfo.id}/adjust`, {
                          method: 'POST', headers, body: JSON.stringify({ action: 'add_coins', value: amount })
                        });
                        if (res.ok) {
                          const d = await res.json();
                          setSelectedPlayer(d.progress);
                          showNotification(`Added ${amount} coins`, 'success');
                        }
                      } catch (e) { showNotification('Error', 'error'); }
                    }} className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-lg text-xs font-semibold">
                      +{amount}
                    </button>
                  ))}
                </div>

                {/* Unlock background */}
                <div className="flex gap-2 mb-2 items-center flex-wrap">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Unlock BG:</span>
                  <select id="unlockBgSelect"
                    className={`flex-1 px-2 py-1 rounded-lg text-xs ${inputBg} border ${border}`}>
                    <option value="">Select background...</option>
                    {backgrounds.map(b => (
                      <option key={b.id} value={b.id} disabled={(selectedPlayer.unlocked_backgrounds || []).includes(b.id)}>
                        {b.name} {(selectedPlayer.unlocked_backgrounds || []).includes(b.id) ? '(owned)' : ''}
                      </option>
                    ))}
                  </select>
                  <button onClick={async () => {
                    const sel = document.getElementById('unlockBgSelect');
                    if (!sel.value) return;
                    try {
                      const res = await fetch(`${API}/admin/word-puzzle/players/${selectedPlayerInfo.id}/adjust`, {
                        method: 'POST', headers, body: JSON.stringify({ action: 'unlock_background', value: sel.value })
                      });
                      if (res.ok) { const d = await res.json(); setSelectedPlayer(d.progress); showNotification('Background unlocked', 'success'); }
                    } catch (e) { showNotification('Error', 'error'); }
                  }} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-semibold">
                    Unlock
                  </button>
                </div>

                {/* Unlock hex style */}
                <div className="flex gap-2 items-center flex-wrap">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Unlock Hex:</span>
                  <select id="unlockHexSelect"
                    className={`flex-1 px-2 py-1 rounded-lg text-xs ${inputBg} border ${border}`}>
                    <option value="">Select hex style...</option>
                    {hexStyles.map(h => (
                      <option key={h.id} value={h.id} disabled={(selectedPlayer.unlocked_hex_styles || []).includes(h.id)}>
                        {h.name} {(selectedPlayer.unlocked_hex_styles || []).includes(h.id) ? '(owned)' : ''}
                      </option>
                    ))}
                  </select>
                  <button onClick={async () => {
                    const sel = document.getElementById('unlockHexSelect');
                    if (!sel.value) return;
                    try {
                      const res = await fetch(`${API}/admin/word-puzzle/players/${selectedPlayerInfo.id}/adjust`, {
                        method: 'POST', headers, body: JSON.stringify({ action: 'unlock_hex_style', value: sel.value })
                      });
                      if (res.ok) { const d = await res.json(); setSelectedPlayer(d.progress); showNotification('Hex style unlocked', 'success'); }
                    } catch (e) { showNotification('Error', 'error'); }
                  }} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-semibold">
                    Unlock
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── IMAGE PICKER MODAL ── */}
      {showImagePicker && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowImagePicker(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '90%', maxWidth: 700, maxHeight: '80vh',
            background: isDarkMode ? '#1e293b' : '#fff',
            borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: isDarkMode ? '#e2e8f0' : '#1e293b' }}>📁 Pick Image from Library</span>
              <button onClick={() => setShowImagePicker(false)} style={{ fontSize: 18, color: isDarkMode ? '#94a3b8' : '#64748b', cursor: 'pointer', background: 'none', border: 'none' }}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ padding: '8px 16px', display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}>
              {[{ id: 'icons', label: '🎨 Icons' }, { id: 'avatars', label: '👤 Avatars' },
                ...pickerImages.folders.map(f => ({ id: `folder-${f.id}`, label: `📂 ${f.name}` }))
              ].map(tab => (
                <button key={tab.id} onClick={() => setPickerTab(tab.id)}
                  style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
                    background: pickerTab === tab.id ? '#3b82f6' : isDarkMode ? '#334155' : '#e2e8f0',
                    color: pickerTab === tab.id ? '#fff' : isDarkMode ? '#cbd5e1' : '#475569'
                  }}>{tab.label}</button>
              ))}
            </div>

            {/* Image Grid */}
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              {pickerLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: isDarkMode ? '#94a3b8' : '#64748b' }}>Loading images...</div>
              ) : (() => {
                let images = [];
                if (pickerTab === 'icons') images = pickerImages.icons;
                else if (pickerTab === 'avatars') images = pickerImages.avatars;
                else {
                  const folderId = pickerTab.replace('folder-', '');
                  const folder = pickerImages.folders.find(f => f.id === folderId);
                  images = folder?.assets || [];
                }
                if (images.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: isDarkMode ? '#94a3b8' : '#64748b' }}>No images found</div>;
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                    {images.map((img, i) => (
                      <button key={img.id || i} onClick={() => {
                        setBgValue(img.url);
                        setBgType('image');
                        setShowImagePicker(false);
                        showNotification(`Selected: ${img.name || 'image'}`, 'success');
                      }} style={{
                        border: `2px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                        borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: 'none', padding: 0,
                        transition: 'border-color 0.2s'
                      }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = isDarkMode ? '#334155' : '#e2e8f0'}>
                        <img src={`${API.replace('/api', '')}${img.url}`} alt={img.name || ''}
                          style={{ width: '100%', height: 70, objectFit: 'cover', display: 'block' }} />
                        <div style={{
                          padding: '3px 4px', fontSize: 9, fontWeight: 600,
                          color: isDarkMode ? '#cbd5e1' : '#475569',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          background: isDarkMode ? '#1e293b' : '#f8fafc'
                        }}>{img.name || 'Untitled'}</div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRMATION MODAL ── */}
      {shopDeleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div className={`${bg} rounded-xl border ${border} p-6 max-w-sm w-full mx-4`}
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div className="text-center mb-4">
              <div className="text-3xl mb-2">🗑️</div>
              <h3 className={`text-sm font-bold ${text}`}>Delete "{shopDeleteConfirm.name}"?</h3>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShopDeleteConfirm(null)}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                Cancel
              </button>
              <button onClick={async () => {
                const { id, type } = shopDeleteConfirm;
                const endpoint = type === 'hex-style' ? 'hex-styles' : 'backgrounds';
                const reloadFn = type === 'hex-style' ? loadHexStyles : loadBackgrounds;
                try {
                  const res = await fetch(`${API}/admin/word-puzzle/${endpoint}/${id}`, { method: 'DELETE', headers });
                  if (res.ok) { showNotification('Deleted', 'success'); reloadFn(); }
                  else { const err = await res.json().catch(() => ({})); showNotification(err.detail || 'Error', 'error'); }
                } catch (e) { showNotification('Error', 'error'); }
                setShopDeleteConfirm(null);
              }}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Icon Picker Modal */}
      {iconPickerOpen && (
        <IconPicker
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          onClose={() => setIconPickerOpen(false)}
          onPick={(url) => handleIconSelect(url)}
        />
      )}
    </div>
  );
};

export default WordPuzzleAdmin;
